/**
 * NIVARI Cetragon — Cart Integrity (M7B).
 *
 * A configured Cetragon is one Watch line + one Primary Supply line + zero
 * or more Extra Supply lines, linked ONLY by the private line-item
 * properties CartAdapter already writes (see /CLAUDE.md, Cart):
 *
 *   _nivari_configuration_schema = "cetragon-v1"
 *   _nivari_configuration_id     = opaque UUID, shared by every line of one add
 *   _nivari_configuration_role   = "watch" | "primary_supply" | "extra_supply"
 *
 * This is a GLOBAL, storefront-wide asset — not part of nivari-configurator.js
 * on purpose. A configured Cetragon lives in the cart long after the
 * customer leaves the Configurator page (Cart Drawer, /cart, any other
 * page), so cart integrity has to run everywhere, loaded once from
 * theme.liquid. nivari-configurator.js stays Configurator-page-specific;
 * CartAdapter/buildCartItems/configurationExistsInCart are NOT duplicated
 * or moved here — this file only protects an already-added group.
 *
 * Responsibilities:
 *   1. Classify already-rendered `<line-item>` elements (done in Liquid —
 *      see snippets/line-item.liquid; this file never classifies via DOM).
 *   2. Audit the REAL cart (GET cart.js — the Ajax cart object is commerce
 *      truth, never DOM) for structurally invalid/orphaned configuration
 *      groups, and reflect that (fail-closed: mark + disable checkout,
 *      never silently repair/re-add/guess a replacement).
 *   3. Handle "Remove configuration" clicks as ONE cart/update.js request
 *      that zeroes every line of that configuration group by its current
 *      cart line KEY (never variant_id — the same Supply variant can appear
 *      in two different configuration groups; see below), with the same
 *      ambiguous-mutation recovery discipline as CartAdapter (M7A.1): never
 *      auto-retry the POST, reconcile via a read-only cart.js fetch instead.
 *
 * M7B quantity contract: a configuration group is ALWAYS quantity 1 on every
 * line. There is no stepper. Two identical watches are two independent
 * Add-to-Cart actions -> two configuration IDs -> two independent groups,
 * each removable independently. This file never sends cart/update.js to
 * INCREASE a configured line's quantity, and never chains sequential
 * updates to simulate one.
 */
(function () {
  "use strict";

  const NIVARI_SCHEMA = "cetragon-v1";
  const NIVARI_ROLES = ["watch", "primary_supply", "extra_supply"];
  const CHECKOUT_BUTTON_SELECTOR = 'button[name="checkout"]';
  const INTEGRITY_DISABLED_ATTR = "nivariIntegrityDisabled"; // -> data-nivari-integrity-disabled

  // ---------------------------------------------------------------------
  // Pure helpers — no DOM, no fetch, no mutation. Exercised directly by
  // synthetic tests via the module.exports guard at the bottom of this file.
  // ---------------------------------------------------------------------

  /**
   * Groups a raw Shopify cart.js object's lines by `_nivari_configuration_id`
   * — only lines carrying the exact `_nivari_configuration_schema ===
   * "cetragon-v1"` contract are considered at all; every other cart line
   * (including a Cetragon Bracelet & Straps variant added OUTSIDE the
   * Configurator, with no NIVARI properties) is invisible to this function.
   * Never inspects the DOM — the cart object is commerce truth.
   *
   * @param {{items: Array<object>}} cart
   * @returns {Map<string, {id: string, lines: object[], watchLines: object[], primarySupplyLines: object[], extraSupplyLines: object[], unknownRoleLines: object[]}>}
   */
  function parseNivariConfigurationGroups(cart) {
    const groups = new Map();
    if (!cart || !Array.isArray(cart.items)) {
      return groups;
    }

    for (const line of cart.items) {
      const properties = line && line.properties;
      if (!properties || properties._nivari_configuration_schema !== NIVARI_SCHEMA) {
        continue;
      }
      const id = properties._nivari_configuration_id;
      if (!id) {
        continue;
      }

      if (!groups.has(id)) {
        groups.set(id, { id, lines: [], watchLines: [], primarySupplyLines: [], extraSupplyLines: [], unknownRoleLines: [] });
      }
      const group = groups.get(id);
      group.lines.push(line);

      const role = properties._nivari_configuration_role;
      if (role === "watch") {
        group.watchLines.push(line);
      } else if (role === "primary_supply") {
        group.primarySupplyLines.push(line);
      } else if (role === "extra_supply") {
        group.extraSupplyLines.push(line);
      } else {
        group.unknownRoleLines.push(line);
      }
    }

    return groups;
  }

  /**
   * Launch-valid group contract (see /CLAUDE.md, Cart): exactly one Watch
   * line, exactly one Primary Supply line, zero or more Extra Supply lines,
   * no unknown-role lines, and every component line at quantity 1 with the
   * exact schema. Does NOT validate price or Founder tier, and never
   * re-resolves the Watch/Supply variants — identity/pricing is
   * VariantResolver/SupplyResolver/CommercePlan's job, not this audit's.
   *
   * @param {ReturnType<typeof parseNivariConfigurationGroups> extends Map<string, infer G> ? G : never} group
   * @returns {{valid: boolean, reason: string|null}}
   */
  function validateNivariConfigurationGroup(group) {
    if (!group) {
      return { valid: false, reason: "missing_group" };
    }
    if (group.unknownRoleLines.length > 0) {
      return { valid: false, reason: "unknown_role" };
    }
    if (group.watchLines.length === 0) {
      return { valid: false, reason: "missing_watch" };
    }
    if (group.watchLines.length > 1) {
      return { valid: false, reason: "duplicate_watch" };
    }
    if (group.primarySupplyLines.length === 0) {
      return { valid: false, reason: "missing_primary_supply" };
    }
    if (group.primarySupplyLines.length > 1) {
      return { valid: false, reason: "duplicate_primary_supply" };
    }
    for (const line of group.lines) {
      if (line.quantity !== 1) {
        return { valid: false, reason: "invalid_quantity" };
      }
      if (!line.properties || line.properties._nivari_configuration_schema !== NIVARI_SCHEMA) {
        return { valid: false, reason: "malformed_schema" };
      }
    }
    return { valid: true, reason: null };
  }

  /**
   * Builds the exact `cart/update.js` `updates` payload to remove one
   * configuration group — keyed by each matching line's CURRENT `line.key`,
   * never `variant_id`. The same Supply variant can exist in two different
   * configuration groups at once (Config A's Steel and Config B's Steel are
   * the same Shopify variant, different cart lines); only the line key
   * uniquely identifies THIS group's line. Returns an empty object (no
   * destructive update) if the cart has no lines for this configuration id
   * — callers must treat that as "nothing to remove", never send it.
   *
   * @param {{items: Array<object>}} cart a FRESH cart.js object — never a cached/stale one
   * @param {string} configurationId
   * @returns {Record<string, 0>}
   */
  function buildConfigurationRemovalUpdates(cart, configurationId) {
    const updates = {};
    if (!cart || !Array.isArray(cart.items) || !configurationId) {
      return updates;
    }
    for (const line of cart.items) {
      const properties = line && line.properties;
      if (properties && properties._nivari_configuration_schema === NIVARI_SCHEMA && properties._nivari_configuration_id === configurationId && line.key) {
        updates[line.key] = 0;
      }
    }
    return updates;
  }

  // ---------------------------------------------------------------------
  // Effectful engine
  // ---------------------------------------------------------------------

  /**
   * @param {{fetchImpl?: typeof fetch, documentTarget?: Document, windowRef?: Window}} [deps]
   */
  function createCartIntegrity({ fetchImpl = (...args) => fetch(...args), documentTarget = document, windowRef = window } = {}) {
    const inFlightConfigurationIds = new Set();

    async function fetchCart() {
      try {
        const response = await fetchImpl(`${windowRef.Shopify.routes.root}cart.js`);
        if (!response.ok) {
          return null;
        }
        return await response.json();
      } catch {
        return null;
      }
    }

    function announce(elementId, message) {
      const el = documentTarget.getElementById(elementId);
      if (el) {
        el.textContent = message;
      }
    }

    // --- checkout button safety --------------------------------------

    function setCheckoutDisabled(shouldDisable) {
      documentTarget.querySelectorAll(CHECKOUT_BUTTON_SELECTOR).forEach((button) => {
        if (shouldDisable) {
          if (!button.disabled) {
            button.disabled = true;
            button.dataset[INTEGRITY_DISABLED_ATTR] = "true";
          }
          // Already disabled (by us or by anything else) — leave it alone,
          // never claim ownership of a disable we didn't cause.
        } else if (button.dataset[INTEGRITY_DISABLED_ATTR] === "true") {
          button.disabled = false;
          delete button.dataset[INTEGRITY_DISABLED_ATTR];
        }
        // Disabled for a reason that isn't ours — never force-enable it.
      });
    }

    // --- DOM marking (per configured line, never relying on adjacency) ---

    function markGroupDom(configurationId, valid) {
      documentTarget.querySelectorAll(`[data-nivari-configuration-id="${cssEscape(configurationId)}"]`).forEach((lineEl) => {
        lineEl.classList.toggle("nivari-cart-line--invalid", !valid);
      });
    }

    function cssEscape(value) {
      if (windowRef.CSS && typeof windowRef.CSS.escape === "function") {
        return windowRef.CSS.escape(value);
      }
      return String(value).replace(/["\\]/g, "\\$&");
    }

    // --- audit ----------------------------------------------------------

    /**
     * Re-derives every configuration group's validity from a fresh cart.js
     * fetch and reflects it in the DOM/checkout state. Only fetches when
     * `force` is true OR there is DOM evidence of a configured line — never
     * polls, never runs on an interval; only ever called from a lifecycle
     * event (see init() below).
     */
    async function runAudit({ force = false } = {}) {
      const hasEvidence = !!documentTarget.querySelector(`[data-nivari-configuration-schema="${NIVARI_SCHEMA}"]`);
      if (!hasEvidence && !force) {
        // Nothing configured currently rendered anywhere on this page —
        // release any stale disable we might still be holding and stop.
        setCheckoutDisabled(false);
        return;
      }

      const cart = await fetchCart();
      if (!cart) {
        return; // couldn't verify — leave current state exactly as-is, never guess
      }

      const groups = parseNivariConfigurationGroups(cart);
      let anyInvalid = false;
      groups.forEach((group, configurationId) => {
        const { valid } = validateNivariConfigurationGroup(group);
        markGroupDom(configurationId, valid);
        if (!valid) {
          anyInvalid = true;
        }
      });

      // A group that has fully vanished from the cart (e.g. just removed)
      // can't still be "invalid" — clear any stale marker left on a DOM
      // element whose configuration id no longer appears in `groups` at
      // all, so a lingering element (should the surface not have replaced
      // its content) doesn't keep showing a stale warning.
      documentTarget.querySelectorAll(".nivari-cart-line--invalid").forEach((lineEl) => {
        const id = lineEl.getAttribute("data-nivari-configuration-id");
        if (!id || !groups.has(id)) {
          lineEl.classList.toggle("nivari-cart-line--invalid", false);
        }
      });

      setCheckoutDisabled(anyInvalid);
    }

    // --- whole-group removal ---------------------------------------------

    function sectionsUrl() {
      const path = `${windowRef.location.pathname}${windowRef.location.search}`;
      return path.startsWith("/") ? path : "/";
    }

    function setGroupBusy(configurationId, busy) {
      documentTarget.querySelectorAll(`[data-nivari-remove-configuration][data-nivari-configuration-id="${cssEscape(configurationId)}"]`).forEach((button) => {
        button.disabled = busy;
        if (busy) {
          button.setAttribute("aria-busy", "true");
        } else {
          button.removeAttribute("aria-busy");
        }
      });
    }

    /** Read-only reconciliation for an ambiguous update.js outcome — never re-POSTs. */
    async function reconcileRemoval(configurationId) {
      const cart = await fetchCart();
      if (!cart) {
        return { ok: false, code: "network_error" };
      }
      const groups = parseNivariConfigurationGroups(cart);
      if (!groups.has(configurationId)) {
        return { ok: true, cart, recovered: true };
      }
      return { ok: false, code: "network_error" };
    }

    async function performRemoval(configurationId) {
      // Always re-fetch — never trust stale DOM-derived line keys.
      const currentCart = await fetchCart();
      if (!currentCart) {
        return { ok: false, code: "network_error" };
      }

      const updates = buildConfigurationRemovalUpdates(currentCart, configurationId);
      if (Object.keys(updates).length === 0) {
        // Nothing to remove — never send a destructive update request for
        // a group that isn't actually in the cart (already removed elsewhere, etc).
        return { ok: false, code: "missing_group" };
      }

      const sectionsToBundle = [];
      documentTarget.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
      );
      const sections = [...new Set(sectionsToBundle)];

      let response;
      try {
        response = await fetchImpl(`${windowRef.Shopify.routes.root}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ updates, sections: sections.join(","), sections_url: sectionsUrl() }),
        });
      } catch {
        // Ambiguous (M7A.1-style): the POST may have landed server-side
        // before the connection failed. Reconcile, never re-POST.
        return await reconcileRemoval(configurationId);
      }

      let responseJson;
      try {
        responseJson = await response.json();
      } catch {
        if (!response.ok) {
          return { ok: false, code: "shopify_error" };
        }
        return await reconcileRemoval(configurationId);
      }

      if (!response.ok) {
        // Definite, parseable Shopify error — nothing ambiguous, no recovery needed.
        return { ok: false, code: "shopify_error" };
      }

      // cart/update.js's response IS the full cart object (with `.sections`
      // attached, since we requested them) — no separate cart.js re-fetch needed.
      return { ok: true, cart: responseJson, recovered: false };
    }

    async function removeConfiguration(configurationId) {
      if (inFlightConfigurationIds.has(configurationId)) {
        return; // one remove action per configuration at a time — no double POST
      }
      inFlightConfigurationIds.add(configurationId);
      setGroupBusy(configurationId, true);
      documentTarget.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

      try {
        const result = await performRemoval(configurationId);

        if (!result.ok) {
          announce("error-announcement", "Could not remove this configuration. Please try again.");
          return;
        }

        announce("status-announcement", "Configuration removed from cart.");

        if (windowRef.themeVariables?.settings?.pageType === "cart") {
          // Mirrors Prestige's own native quantity-change behavior exactly
          // (see theme.js) — simplest correct option, no bespoke partial
          // table re-render.
          windowRef.location.reload();
          return;
        }

        if (result.recovered) {
          // No bundled section HTML available for a reconciled/ambiguous
          // outcome — use Prestige's own cart:refresh recovery mechanism
          // (CartCount/CartDot/CartDrawer all re-fetch their own state from
          // the server; see /CLAUDE.md and theme.js). init()'s own
          // cart:refresh listener below re-runs the audit — no separate
          // explicit call needed here.
          documentTarget.dispatchEvent(new CustomEvent("cart:refresh"));
        } else {
          result.cart.sections = result.cart.sections || {};
          documentTarget.documentElement.dispatchEvent(
            new CustomEvent("cart:change", {
              bubbles: true,
              // Deliberately NOT "variant:add" — this is a removal, not an
              // add, and the drawer must not force itself open as if a new
              // item had just been added. init()'s own cart:change listener
              // re-runs the audit — no separate explicit call needed here.
              detail: { baseEvent: "nivari-configuration:remove", cart: result.cart },
            })
          );
        }
      } finally {
        documentTarget.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
        inFlightConfigurationIds.delete(configurationId);
        setGroupBusy(configurationId, false);
      }
    }

    // --- wiring -----------------------------------------------------------

    function onClick(event) {
      const button = event.target.closest("[data-nivari-remove-configuration]");
      if (!button) {
        return;
      }
      event.preventDefault();
      const configurationId = button.dataset.nivariConfigurationId;
      if (!configurationId) {
        return;
      }
      removeConfiguration(configurationId);
    }

    function init() {
      runAudit();
      documentTarget.addEventListener("cart:change", () => runAudit({ force: true }));
      documentTarget.addEventListener("cart-drawer:refreshed", () => runAudit({ force: true }));
      // Covers the ambiguous/recovered removal path (see removeConfiguration
      // above), which dispatches cart:refresh instead of cart:change since
      // no bundled section HTML is available for that outcome.
      documentTarget.addEventListener("cart:refresh", () => runAudit({ force: true }));
      documentTarget.addEventListener("click", onClick);
    }

    return { init, runAudit, removeConfiguration };
  }

  if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => createCartIntegrity().init());
    } else {
      createCartIntegrity().init();
    }
  }

  // Testability hook only — see the identical pattern/rationale in
  // nivari-configurator.js. `module` never exists in the browser.
  if (typeof module !== "undefined" && module.exports) {
    module.exports = {
      parseNivariConfigurationGroups,
      validateNivariConfigurationGroup,
      buildConfigurationRemovalUpdates,
      createCartIntegrity,
      NIVARI_SCHEMA,
      NIVARI_ROLES,
    };
  }
})();
